import type { Schema } from '@dealdesk/api-schema';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { FormErrorSummary, MoneyField, SelectField, TextField, applyServerErrors, zMoney } from '@/business';
import { Button, DeleteIcon, Drawer, DrawerContent, IconButton, PlusIcon, toast } from '@/design-system';
import { CustomerDocumentDialog, customerQuery } from '@/features/customers';
import { apiErrorMessage, isApiError, type ApiError } from '@/shared/api';
import { useCan } from '@/shared/auth';

import { useReplaceIncome } from '../api/mutations';
import { INCOME_KINDS } from '../forms/dealForm';

const KINDS = INCOME_KINDS;
const schema = z.object({
  components: z.array(
    z.object({
      id: z.string().optional(),
      employmentId: z.string(),
      kind: z.enum(INCOME_KINDS),
      label: z.string().optional(),
      monthlyAmount: zMoney({ min: '0.00' }),
      considered: z.enum(['yes', 'no', 'unknown']),
      /** A customer document (salary certificate, payslips …) evidencing the component; '' = none. */
      evidenceDocumentId: z.string(),
      note: z.string().optional(),
    }),
  ),
});
type Values = z.infer<typeof schema>;

/** Records which income components the bank considered. Salaries are never summed automatically. */
export function IncomeEditor({ deal, application, onClose }: { deal: Schema<'Deal'>; application: Schema<'DealApplication'>; onClose: () => void }) {
  const { t } = useTranslation('deals');
  const save = useReplaceIncome(deal.id);
  const [problem, setProblem] = useState<ApiError | null>(null);
  const [addingDocument, setAddingDocument] = useState(false);
  const can = useCan();
  // Evidence comes from the customer record (reused by every deal), never copied onto the deal.
  const customer = useQuery(customerQuery(deal.customer.id));
  const documents = customer.data?.documents ?? [];
  const evidenceOptions = [{ value: '', label: t('application.incomeEditor.noEvidence') }, ...documents.map((d) => ({ value: d.id, label: d.fileName ? `${d.name} · ${d.fileName}` : d.name }))];
  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { components: application.income.map((i) => ({ id: i.id, employmentId: i.employmentId ?? '', kind: i.kind, label: i.label ?? '', monthlyAmount: i.monthlyAmount, considered: i.consideredByBank === null ? 'unknown' : i.consideredByBank ? 'yes' : 'no', evidenceDocumentId: i.evidenceDocumentId ?? '', note: i.note ?? '' })) },
  });
  const rows = useFieldArray({ control: form.control, name: 'components' });
  const employmentOptions = [{ value: '', label: t('application.incomeEditor.noEmployment') }, ...application.employments.map((e) => ({ value: e.id, label: e.employerName }))];
  const submit = form.handleSubmit(async (v) => {
    setProblem(null);
    try {
      await save.mutateAsync({
        version: application.version,
        components: v.components.map((c) => ({ ...(c.id ? { id: c.id } : {}), ...(c.employmentId ? { employmentId: c.employmentId } : {}), kind: c.kind, ...(c.label?.trim() ? { label: c.label.trim() } : {}), monthlyAmount: c.monthlyAmount, consideredByBank: c.considered === 'unknown' ? null : c.considered === 'yes', evidenceDocumentId: c.evidenceDocumentId || null, ...(c.note?.trim() ? { note: c.note.trim() } : {}) })),
      });
      toast.done(t('application.incomeEditor.saved'));
      onClose();
    } catch (e) {
      if (isApiError(e)) {
        applyServerErrors(form, e, v.components.map((_, i) => `components.${i}.evidenceDocumentId` as const));
        setProblem(e);
      }
    }
  });
  return (
    <Drawer open onOpenChange={(o) => !o && onClose()}>
      <DrawerContent
        width="wide"
        title={t('application.incomeEditor.title')}
        description={t('application.incomeEditor.body')}
        footer={
          <>
            <Button onClick={onClose}>{t('actions.cancel', { ns: 'common' })}</Button>
            <Button variant="primary" loading={save.isPending} onClick={() => void submit()}>
              {t('application.incomeEditor.save')}
            </Button>
          </>
        }
      >
        <form onSubmit={(e) => void submit(e)} noValidate className="flex flex-col gap-4 px-5 py-4">
          {problem ? <FormErrorSummary title={apiErrorMessage(problem)} /> : null}
          {rows.fields.map((row, i) => (
            <fieldset key={row.id} className="flex flex-col gap-3 border-b border-border pb-4">
              <legend className="sr-only">{t('application.incomeEditor.row', { n: i + 1 })}</legend>
              <div className="grid grid-cols-[1fr_1fr_auto] items-end gap-3">
                <SelectField control={form.control} name={`components.${i}.kind`} label={t('application.incomeEditor.kind')} options={KINDS.map((k) => ({ value: k, label: t(`capture.income.kinds.${k}`) }))} />
                <SelectField control={form.control} name={`components.${i}.employmentId`} label={t('application.incomeEditor.employment')} options={employmentOptions} />
                <IconButton label={t('application.incomeEditor.remove')} icon={<DeleteIcon />} onClick={() => rows.remove(i)} />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <MoneyField control={form.control} name={`components.${i}.monthlyAmount`} label={t('application.incomeEditor.monthly')} />
                <SelectField control={form.control} name={`components.${i}.considered`} label={t('application.incomeEditor.considered')} options={(['unknown', 'yes', 'no'] as const).map((c) => ({ value: c, label: t(`workflow.income.considered.${c}`, { ns: 'common' }) }))} />
                <TextField control={form.control} name={`components.${i}.label`} label={t('application.incomeEditor.label')} optional />
              </div>
              <div className="grid grid-cols-[2fr_1fr] gap-3">
                <SelectField control={form.control} name={`components.${i}.evidenceDocumentId`} label={t('application.incomeEditor.evidence')} options={evidenceOptions} help={t('application.incomeEditor.evidenceHelp')} />
                <TextField control={form.control} name={`components.${i}.note`} label={t('application.incomeEditor.note')} optional />
              </div>
            </fieldset>
          ))}
          <div className="flex flex-wrap gap-2">
            <Button size="sm" onClick={() => rows.append({ employmentId: application.employments[0]?.id ?? '', kind: 'allowance', label: '', monthlyAmount: '0.00', considered: 'unknown', evidenceDocumentId: '', note: '' })}>
              <PlusIcon size={14} />
              {t('application.incomeEditor.add')}
            </Button>
            {can('document.upload') ? (
              <Button size="sm" variant="ghost" onClick={() => setAddingDocument(true)}>
                {t('application.incomeEditor.addEvidence')}
              </Button>
            ) : null}
          </div>
          <button type="submit" hidden />
        </form>
        {addingDocument ? <CustomerDocumentDialog customerId={deal.customer.id} document={null} onClose={() => setAddingDocument(false)} /> : null}
      </DrawerContent>
    </Drawer>
  );
}
